import React, { useState } from 'react';
import { Button } from '../Button/Button';
import { Icon } from '../Icon/Icon';
import { GROWTH_MARKET_META } from '../../growth-market/config';
import { MI_HEADER_STATS, MI_TABS } from '../../growth-market/mockData';
import type { MiHeaderStat, MiMarketData, MiTabId } from '../../growth-market/types';
import { miEyebrowClass } from './MarketIntelligenceParts';
import {
  MiTabAds,
  MiTabOverview,
  MiTabSegments,
  MiTabSeo,
} from './MarketIntelligenceTabs';
import './growth-market.css';

export interface MarketIntelligenceViewProps {
  data?: MiMarketData;
  headerStats?: MiHeaderStat[];
  loading?: boolean;
  onViewAutopilotLog?: () => void;
  onExport?: () => void;
}

export function MarketIntelligenceView({
  data,
  headerStats: headerStatsProp,
  loading = false,
  onViewAutopilotLog,
  onExport,
}: MarketIntelligenceViewProps = {}) {
  const meta = GROWTH_MARKET_META;
  const connected = data !== undefined;
  const headerStats = connected ? data.headerStats : (headerStatsProp ?? MI_HEADER_STATS);
  const tabData = connected ? data : undefined;
  const [tab, setTab] = useState<MiTabId>('overview');

  return (
    <div className="growth-market-root relative mx-auto w-full max-w-[1120px] px-[30px] pb-[90px] pt-[26px]">
      <div className="mb-[18px] flex flex-wrap items-start justify-between gap-5">
        <div className="max-w-[560px]">
          <div className="mb-2 flex items-center gap-2">
            <Icon kind="globe" size={13} stroke={2} className="text-[var(--ai-3)]" />
            <span className="font-[var(--font-mono)] text-[11px] font-semibold uppercase tracking-[0.15em] text-[var(--ai-3)]">
              {meta.eyebrow}
            </span>
          </div>
          <h1 className="m-0 font-[var(--font-display)] text-[clamp(26px,3vw,33px)] font-semibold leading-[1.08] tracking-[-0.035em] text-[var(--ink-0)]">
            {meta.titleLead} <span className="elva-spectrum">{meta.titleAccent}</span>
          </h1>
          <p className="mt-2.5 text-[13.5px] leading-[1.55] text-[var(--ink-2)]">
            {meta.description}
            {loading ? ' · Updating…' : ''}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-4">
          {headerStats.map((stat, index) => (
            <React.Fragment key={stat.label}>
              {index > 0 ? <span className="h-9 w-px bg-[var(--cream-2)]" /> : null}
              <div className="text-right">
                <div className={miEyebrowClass}>{stat.label}</div>
                <div
                  className={[
                    'mt-[3px] font-[var(--font-display)] tracking-[-0.03em]',
                    stat.accent
                      ? 'text-sm font-semibold text-[var(--success)]'
                      : 'text-[22px] font-extrabold text-[var(--ink-0)]',
                  ].join(' ')}
                >
                  {stat.value}
                </div>
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>

      <div className="mb-6 flex flex-wrap items-center gap-[11px] rounded-[var(--r-md)] border border-[color-mix(in_srgb,var(--ai-3)_22%,transparent)] bg-[var(--ai-tint)] px-[18px] py-[11px]">
        <span className="elva-live h-2 w-2" />
        <span className="text-[13px] text-[var(--ink-1)]">
          Ad Autopilot is <b className="font-bold">active</b> and using this intelligence to optimize your
          campaigns in real-time.
        </span>
        <button
          type="button"
          onClick={onViewAutopilotLog}
          className="gd-link ml-auto inline-flex cursor-pointer items-center gap-1 border-0 bg-transparent text-[12.5px] font-semibold text-[var(--ai-3)]"
        >
          View Autopilot Log <Icon kind="arrow-r" size={12} stroke={2.2} />
        </button>
      </div>

      <div className="mb-6 inline-flex flex-wrap gap-[3px] rounded-[var(--r-pill)] border border-[var(--cream-2)] bg-[var(--cream-1)] p-1 shadow-[var(--sh-1)]">
        {MI_TABS.map((tabDef) => {
          const active = tab === tabDef.id;
          return (
            <button
              key={tabDef.id}
              type="button"
              onClick={() => setTab(tabDef.id)}
              className={[
                'ma-tab cursor-pointer whitespace-nowrap rounded-[var(--r-pill)] border-0 px-4 py-[7px] font-[var(--font-body)] text-[12.5px]',
                active
                  ? 'ma-tab--on bg-[var(--surface-mute)] font-semibold text-[var(--ink-0)]'
                  : 'bg-transparent font-medium text-[var(--ink-3)]',
              ].join(' ')}
            >
              {tabDef.label}
            </button>
          );
        })}
      </div>

      <div className="elva-fade" key={tab}>
        {tab === 'overview' && <MiTabOverview {...(tabData ? { data: tabData } : {})} />}
        {tab === 'ads' && <MiTabAds {...(tabData ? { data: tabData } : {})} />}
        {tab === 'seo' && <MiTabSeo {...(tabData ? { data: tabData } : {})} />}
        {tab === 'segments' && <MiTabSegments {...(tabData ? { data: tabData } : {})} />}
      </div>

      <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-[var(--cream-2)] pt-4">
        <span className="font-[var(--font-mono)] text-xs text-[var(--ink-4)]">{meta.footerNote}</span>
        <Button size="sm" variant="secondary" iconLeft="download" onClick={onExport}>
          Export report
        </Button>
      </div>
    </div>
  );
}
